import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { TUploadedFile } from '@onesource/schemas';
import { useCallback, useMemo, useState } from 'react';
import { MdImage } from 'react-icons/md';

import { MediaGridSkeleton } from './media-grid-skeleton';
import { MediaPreviewDialog } from './media-preview-dialog';
import { MediaThumbnail } from './media-thumbnail';
import { useMediaDelete } from './use-media-delete';

export type TMediaGridProps = {
  files: TUploadedFile[];
  isLoading: boolean;
};

export function MediaGrid({ files, isLoading }: TMediaGridProps) {
  const theme = useTheme();
  const [previewFile, setPreviewFile] = useState<TUploadedFile | null>(null);
  const { deleteFile } = useMediaDelete(deletedFile => {
    if (previewFile?.key === deletedFile.key) {
      setPreviewFile(null);
    }
  });

  const handlePreview = useCallback((file: TUploadedFile) => setPreviewFile(file), []);
  const handleClosePreview = useCallback(() => setPreviewFile(null), []);
  const handleNavigate = useCallback((file: TUploadedFile) => setPreviewFile(file), []);
  const grid = useMemo(
    () =>
      files.map(file => (
        <Grid item key={file.key} xs={6} sm={4} md={3} lg={2}>
          <MediaThumbnail
            file={file}
            isSelected={false}
            onSelect={handlePreview}
            onPreview={handlePreview}
            onDelete={deleteFile}
          />
        </Grid>
      )),
    [files, handlePreview, deleteFile],
  );

  if (!isLoading && files.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 10,
          gap: 1.5,
          color: 'text.secondary',
        }}
      >
        <MdImage size={56} color={theme.palette.action.disabled} />
        <Typography variant="body2">No files uploaded yet. Click "Upload" to add files.</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        {/* Real content — always rendered to preserve layout */}
        <Grid container spacing={1.5}>
          {grid}
        </Grid>

        {/* Overlay skeleton covers the grid while loading */}
        {isLoading && <MediaGridSkeleton columns={{ xs: 6, sm: 4, md: 3, lg: 2 }} />}
      </Box>
      <MediaPreviewDialog file={previewFile} onClose={handleClosePreview} files={files} onNavigate={handleNavigate} />
    </>
  );
}
